import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Crop,
  Check,
  X,
  RotateCcw,
  RotateCw,
  Info,
} from 'lucide-react';
import { ImageSource, Point2D, QuadCorners } from '../types';
import { unwarpPerspectiveCorners, cropRectangular, rotateImageSource } from '../services/imageUtils';

interface PerspectiveCropModalProps {
  isOpen: boolean;
  sourceImage: ImageSource;
  onApplyCrop: (croppedImage: ImageSource, corners: QuadCorners) => void;
  onClose: () => void;
  initialCorners?: QuadCorners | null;
  /** Contextual label shown in the modal header (e.g. Editing: Master Restoration v1). */
  editingLabel?: string;
}

type DragHandle = 'topLeft' | 'topRight' | 'bottomRight' | 'bottomLeft' | null;

export const PerspectiveCropModal: React.FC<PerspectiveCropModalProps> = ({
  isOpen,
  sourceImage: initialSourceImage,
  onApplyCrop,
  onClose,
  initialCorners,
  editingLabel,
}) => {
  const [currentImage, setCurrentImage] = useState<ImageSource>(initialSourceImage);
  const [cropMode, setCropMode] = useState<'perspective' | 'rectangle'>('perspective');
  const [corners, setCorners] = useState<QuadCorners>(
    initialCorners || {
      topLeft: { x: 0.08, y: 0.08 },
      topRight: { x: 0.92, y: 0.08 },
      bottomRight: { x: 0.92, y: 0.92 },
      bottomLeft: { x: 0.08, y: 0.92 },
    }
  );
  const [activeHandle, setActiveHandle] = useState<DragHandle>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    setCurrentImage(initialSourceImage);
  }, [initialSourceImage]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const handleResetCorners = () => {
    setCorners({
      topLeft: { x: 0.05, y: 0.05 },
      topRight: { x: 0.95, y: 0.05 },
      bottomRight: { x: 0.95, y: 0.95 },
      bottomLeft: { x: 0.05, y: 0.95 },
    });
  };

  const handleRotateInsideModal = async (dir: 'cw' | 'ccw') => {
    try {
      const delta = dir === 'cw' ? 90 : -90;
      const rotated = await rotateImageSource(currentImage, delta);
      setCurrentImage(rotated);
      handleResetCorners();
    } catch (err) {
      console.error('Modal rotate error:', err);
    }
  };

  const getNormalizedPoint = useCallback((clientX: number, clientY: number): Point2D | null => {
    if (!imgRef.current) return null;
    const rect = imgRef.current.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;

    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    return { x, y };
  }, []);

  const handlePointerDown = (handleName: DragHandle, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveHandle(handleName);
  };

  useEffect(() => {
    if (!activeHandle) return;

    const handlePointerMove = (e: PointerEvent) => {
      const pt = getNormalizedPoint(e.clientX, e.clientY);
      if (!pt) return;

      if (cropMode === 'perspective') {
        setCorners((prev) => ({
          ...prev,
          [activeHandle]: pt,
        }));
      } else {
        setCorners((prev) => {
          const next = { ...prev };
          if (activeHandle === 'topLeft') {
            next.topLeft = pt;
            next.topRight = { x: prev.topRight.x, y: pt.y };
            next.bottomLeft = { x: pt.x, y: prev.bottomLeft.y };
          } else if (activeHandle === 'topRight') {
            next.topRight = pt;
            next.topLeft = { x: prev.topLeft.x, y: pt.y };
            next.bottomRight = { x: pt.x, y: prev.bottomRight.y };
          } else if (activeHandle === 'bottomRight') {
            next.bottomRight = pt;
            next.bottomLeft = { x: prev.bottomLeft.x, y: pt.y };
            next.topRight = { x: pt.x, y: prev.topRight.y };
          } else if (activeHandle === 'bottomLeft') {
            next.bottomLeft = pt;
            next.topLeft = { x: pt.x, y: prev.topLeft.y };
            next.bottomRight = { x: prev.bottomRight.x, y: pt.y };
          }
          return next;
        });
      }
    };

    const handlePointerUp = () => {
      setActiveHandle(null);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [activeHandle, cropMode, getNormalizedPoint]);

  const handleApply = async () => {
    setIsProcessing(true);
    try {
      if (cropMode === 'perspective') {
        const unwarped = await unwarpPerspectiveCorners(currentImage, corners);
        onApplyCrop(unwarped, corners);
      } else {
        const minX = Math.min(corners.topLeft.x, corners.bottomLeft.x);
        const minY = Math.min(corners.topLeft.y, corners.topRight.y);
        const maxX = Math.max(corners.topRight.x, corners.bottomRight.x);
        const maxY = Math.max(corners.bottomLeft.y, corners.bottomRight.y);

        const cropped = await cropRectangular(currentImage, {
          x: minX,
          y: minY,
          width: Math.max(0.05, maxX - minX),
          height: Math.max(0.05, maxY - minY),
        });
        onApplyCrop(cropped, corners);
      }
      onClose();
    } catch (err) {
      console.error('Error applying crop & corner pinning:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  const polygonPoints = `${corners.topLeft.x * 100},${corners.topLeft.y * 100} ${corners.topRight.x * 100},${corners.topRight.y * 100} ${corners.bottomRight.x * 100},${corners.bottomRight.y * 100} ${corners.bottomLeft.x * 100},${corners.bottomLeft.y * 100}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-5 bg-black/90 backdrop-blur-md select-none pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))]">
      <div className="bg-stone-900 border border-stone-700 rounded-2xl max-w-5xl w-full h-[min(92vh,100dvh)] flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in-95">
        {/* Top Header & Modes */}
        <div className="px-5 py-3.5 bg-stone-950 border-b border-stone-800 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center space-x-2.5">
            <div className="p-2 rounded-xl bg-teal-500/10 border border-teal-500/30 text-teal-400">
              <Crop className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-stone-100 flex items-center gap-2">
                Print Boundary & Perspective Corner-Pinning
                <span className="text-[10px] px-2 py-0.5 rounded bg-stone-800 text-teal-300 border border-stone-700 font-mono">
                  {cropMode === 'perspective' ? '4-Point Keystoning' : 'Rectangular Crop'}
                </span>
              </h3>
              <p className="text-[11px] text-stone-400">
                {editingLabel ? (
                  <span className="text-teal-300 font-medium">{editingLabel}. </span>
                ) : null}
                Drag corner handles onto the physical photograph edges to slice off tables, fingers, or album borders.
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Modal Rotate Controls */}
            <div className="inline-flex bg-stone-950 rounded-lg p-0.5 border border-stone-800">
              <button
                type="button"
                onClick={() => handleRotateInsideModal('ccw')}
                className="p-1.5 text-stone-400 hover:text-teal-300 rounded transition-colors"
                title="Rotate 90° Left"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => handleRotateInsideModal('cw')}
                className="p-1.5 text-stone-400 hover:text-teal-300 rounded transition-colors"
                title="Rotate 90° Right"
              >
                <RotateCw className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Mode switch */}
            <div className="inline-flex bg-stone-900 rounded-lg p-0.5 border border-stone-800 text-xs">
              <button
                type="button"
                onClick={() => setCropMode('perspective')}
                className={`px-2.5 py-1 rounded transition-colors ${
                  cropMode === 'perspective'
                    ? 'bg-teal-600 text-stone-950 font-bold'
                    : 'text-stone-400 hover:text-stone-200'
                }`}
              >
                Corner-Pin (Angle Fix)
              </button>
              <button
                type="button"
                onClick={() => setCropMode('rectangle')}
                className={`px-2.5 py-1 rounded transition-colors ${
                  cropMode === 'rectangle'
                    ? 'bg-teal-600 text-stone-950 font-bold'
                    : 'text-stone-400 hover:text-stone-200'
                }`}
              >
                Rectangular Box
              </button>
            </div>

            <button
              type="button"
              onClick={handleResetCorners}
              className="inline-flex items-center space-x-1 px-2.5 py-1.5 text-xs text-stone-400 hover:text-teal-300 hover:bg-stone-800 rounded-lg transition-colors border border-stone-800"
              title="Reset corners to perimeter"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Reset</span>
            </button>

            <button
              type="button"
              onClick={onClose}
              aria-label="Close crop studio"
              className="p-2.5 text-stone-400 hover:text-stone-100 rounded-lg hover:bg-stone-800 transition-colors min-h-11 min-w-11 inline-flex items-center justify-center"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Interactive Editor Workspace */}
        <div
          ref={containerRef}
          className="flex-1 bg-stone-950 relative flex items-center justify-center p-4 sm:p-8 overflow-hidden"
        >
          <div className="relative inline-block max-w-full max-h-full">
            <img
              ref={imgRef}
              src={currentImage.dataUrl}
              alt="Crop Source"
              className="max-h-[min(64vh,100%)] max-w-full object-contain block rounded shadow-2xl pointer-events-none select-none border border-stone-800"
            />

            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <defs>
                <mask id="cornerPinMask">
                  <rect width="100" height="100" fill="white" />
                  <polygon points={polygonPoints} fill="black" />
                </mask>
              </defs>

              <rect
                width="100"
                height="100"
                fill="rgba(0,0,0,0.65)"
                mask="url(#cornerPinMask)"
              />

              <polygon
                points={polygonPoints}
                fill="none"
                stroke="#19aaa8"
                strokeWidth="0.75"
                strokeDasharray="2 1"
              />
            </svg>

            {(
              [
                { key: 'topLeft', label: 'Top-Left', pt: corners.topLeft },
                { key: 'topRight', label: 'Top-Right', pt: corners.topRight },
                { key: 'bottomRight', label: 'Bottom-Right', pt: corners.bottomRight },
                { key: 'bottomLeft', label: 'Bottom-Left', pt: corners.bottomLeft },
              ] as const
            ).map((handle) => (
              <div
                key={handle.key}
                onPointerDown={(e) => handlePointerDown(handle.key, e)}
                style={{
                  left: `${handle.pt.x * 100}%`,
                  top: `${handle.pt.y * 100}%`,
                }}
                className="absolute -translate-x-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing z-20 group"
              >
                <div
                  className={`w-9 h-9 sm:w-8 sm:h-8 rounded-full border-2 border-stone-950 flex items-center justify-center transition-all touch-manipulation ${
                    activeHandle === handle.key
                      ? 'bg-teal-400 scale-125 shadow-[0_0_15px_rgba(25,170,168,0.9)]'
                      : 'bg-teal-500 hover:scale-110 shadow-lg'
                  }`}
                >
                  <div className="w-2 h-2 rounded-full bg-stone-950" />
                </div>

                <div className="hidden sm:block absolute top-9 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded bg-stone-950/90 text-teal-200 text-[10px] font-mono pointer-events-none whitespace-nowrap border border-stone-700 opacity-0 group-hover:opacity-100 transition-opacity">
                  {handle.label}
                </div>
              </div>
            ))}
          </div>

          <div className="absolute top-4 left-4 z-10 flex items-center space-x-2 px-3 py-1.5 bg-stone-900/90 border border-stone-800 rounded-xl text-xs text-stone-300 backdrop-blur-sm max-w-[calc(100%-2rem)]">
            <Info className="w-4 h-4 text-teal-400 shrink-0" />
            <span>
              {cropMode === 'perspective'
                ? 'Drag the four corner handles onto the photo edges.'
                : 'Adjust the box to crop away borders.'}
            </span>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-3 sm:px-5 py-3.5 bg-stone-950 border-t border-stone-800 flex flex-wrap items-center justify-between gap-2 shrink-0">
          <div className="text-xs text-stone-400 hidden sm:block">
            {cropMode === 'perspective'
              ? 'Calculates projective homography to produce a flat rectangular scan.'
              : 'Directly crops the image bounds.'}
          </div>

          <div className="flex items-center space-x-2.5 w-full sm:w-auto justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              className="px-4 py-2.5 min-h-11 rounded-xl text-xs font-medium text-stone-300 hover:text-stone-100 hover:bg-stone-800 transition-colors"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleApply}
              disabled={isProcessing}
              className="inline-flex items-center space-x-2 px-5 py-2 bg-teal-600 hover:bg-teal-500 disabled:bg-stone-800 text-stone-950 font-bold rounded-xl text-xs transition-colors shadow-lg"
            >
              {isProcessing ? (
                <span>Straightening & Cropping...</span>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>Apply Boundary & Straighten</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
